import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';

export const LatestBlogSection: React.FC = () => {
    // Get the 3 most recent posts
    // Get specific posts for home page: Chronically Me, Future of Healthcare, and Shah Happiness Foundation (rightmost)
    const featuredIds = ['8', '1', 'cura-partners-shf'];
    const recentPosts = featuredIds
        .map(id => blogPosts.find(post => post.id === id))
        .filter((post): post is typeof blogPosts[0] => post !== undefined);

    return (
        <section className="py-24 bg-white border-t border-slate-200 relative">
            {/* Full height vertical lines at container edges */}
            <div className="absolute inset-0 max-w-7xl mx-auto pointer-events-none">
                <div className="absolute left-0 top-0 bottom-0 w-px bg-slate-200"></div>
                <div className="absolute right-0 top-0 bottom-0 w-px bg-slate-200"></div>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
                <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
                    <div className="max-w-2xl">
                        <h2 className="text-3xl md:text-4xl text-slate-900 mb-4">
                            From the Journal
                        </h2>
                        <p className="text-lg text-slate-500">
                            Insights on digital health, medical AI, and the future of healthcare accessibility.
                        </p>
                    </div>
                    <Link
                        to="/blog"
                        className="inline-flex items-center font-semibold text-[#003b82] hover:text-blue-700 transition-colors group"
                    >
                        View all articles
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {recentPosts.map((post, index) => (
                        <motion.div
                            key={post.id}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: index * 0.1 }}
                            className="group flex flex-col h-full"
                        >
                            <Link to={`/blog/${post.slug}`} className="block relative overflow-hidden rounded-2xl aspect-[16/10] mb-6">
                                <div className="absolute inset-0 bg-slate-900/0 group-hover:bg-slate-900/5 transition-colors z-10" />
                                <img
                                    src={post.image}
                                    alt={post.title}
                                    className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                                />
                            </Link>

                            <div className="flex flex-col flex-grow">
                                <div className="flex items-center gap-4 text-xs font-medium text-slate-400 mb-3 uppercase tracking-wider">
                                    <span>{post.category}</span>
                                    <span className="w-1 h-1 rounded-full bg-slate-300" />
                                    <span>{post.readTime}</span>
                                </div>

                                <Link to={`/blog/${post.slug}`} className="block mb-3">
                                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#003b82] transition-colors line-clamp-2 leading-tight">
                                        {post.title}
                                    </h3>
                                </Link>

                                <p className="text-slate-500 text-sm line-clamp-2 mb-4 leading-relaxed">
                                    {post.excerpt}
                                </p>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};
